import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  ClipboardList,
  Clock3,
  Gauge,
  Lock,
  LogIn,
  ShieldCheck,
} from 'lucide-react';
import SiteBrandedLoader from '../components/SiteBrandedLoader';
import {
  AssessmentBackdrop,
  BRAND,
  HAIRLINE,
  LikertScale,
  Panel,
  PanelDivider,
  ProgressRing,
  ScaleLegend,
} from '../components/assessment/AssessmentUI';
import { BusinessAxisChart, BusinessOverallGauge, BandChip } from '../components/business/BusinessCharts';
import { useAuth } from '../contexts/AuthContext';
import { businessService } from '../services/api';
import {
  BusinessCaseResult,
  BusinessFormPayload,
  BusinessLanding,
  BusinessProfile,
} from '../types/business';
import { buildLoginUrl, currentPathForNext } from '../utils/redirectUtils';

const fa = (n: number) => n.toLocaleString('fa-IR');

/** برچسب مقیاس ۱ تا ۵، از جدول «مقیاس امتیازدهی» فرم */
const SCALE_LABELS = [
  'وجود ندارد',
  'شروع شده اما پراکنده',
  'اجرا می‌شود ولی ناپایدار',
  'پایدار و قابل اتکا',
  'مستند، سنجش‌پذیر و بهینه',
];

/**
 * فیلدهای بخش A به ترتیب فرم.
 *
 * `max` دقیقاً همان سقفی است که سرور در BusinessProfileDto اعتبارسنجی می‌کند.
 * اگر اینجا سقف نباشد، یک فیلد بلند کل درخواست بخش A را ۴۰۰ می‌کند و چون
 * ذخیره در پس‌زمینه است، کاربر بدون اینکه بفهمد همهٔ اطلاعات پایه را از دست می‌دهد.
 * فیلدهای بلند (long) در دیتابیس ستون Text دارند و سقفی ندارند.
 */
const PROFILE_FIELDS: Array<{
  key: keyof BusinessProfile;
  label: string;
  long?: boolean;
  placeholder?: string;
  max?: number;
}> = [
  { key: 'businessName', label: 'نام کسب‌وکار / برند', max: 200 },
  { key: 'respondentName', label: 'نام و سمت تکمیل‌کننده', max: 200 },
  { key: 'industry', label: 'حوزه فعالیت / صنعت', max: 200 },
  { key: 'city', label: 'شهر / محدوده بازار', max: 200 },
  { key: 'businessAge', label: 'سن کسب‌وکار', placeholder: 'مثلاً ۳ سال', max: 100 },
  {
    key: 'teamSize',
    label: 'تعداد اعضای تمام‌وقت / پاره‌وقت',
    placeholder: 'مثلاً ۴ تمام‌وقت، ۲ پاره‌وقت',
    max: 100,
  },
  { key: 'mainProducts', label: 'محصولات یا خدمات اصلی', long: true },
  { key: 'targetCustomer', label: 'مشتری هدف', long: true },
  { key: 'salesChannels', label: 'کانال‌های اصلی فروش', long: true },
  { key: 'mainChallenge', label: 'مهم‌ترین چالش فعلی', long: true },
  { key: 'twelveMonthGoal', label: 'هدف اصلی ۱۲ ماه آینده', long: true },
];

const FACT_FIELDS: Array<{ key: keyof BusinessProfile; label: string }> = [
  { key: 'positiveTrend', label: 'روند مثبت اصلی' },
  { key: 'worryingTrend', label: 'نگران‌کننده‌ترین روند' },
  { key: 'unmeasuredNumber', label: 'عددی که فعلاً اندازه‌گیری نمی‌شود' },
];

/** سقف کاراکتر هر خانهٔ شاخص عددی — هم‌تراز با BusinessMetricItemDto */
const METRIC_MAX: Record<'current' | 'target' | 'source', number> = {
  current: 200,
  target: 200,
  source: 300,
};

type Mode = 'loading' | 'guest' | 'locked' | 'intro' | 'wizard' | 'result';

/** پیام واحد شکست ذخیره؛ با موفق‌شدن ذخیرهٔ بعدی خودش پاک می‌شود */
const SAVE_FAILED =
  'پاسخ‌های این مرحله ذخیره نشد. اتصال اینترنت را بررسی کنید و دوباره «مرحلهٔ بعد» را بزنید.';

const Shell: React.FC<{ children: React.ReactNode; wide?: boolean }> = ({ children, wide }) => (
  <AssessmentBackdrop>
    <div className={`mx-auto px-4 pb-16 pt-6 sm:px-6 ${wide ? 'max-w-5xl' : 'max-w-3xl'}`}>
      {children}
    </div>
  </AssessmentBackdrop>
);

const inputClass =
  'w-full rounded-xl border bg-white px-3 py-2.5 text-[13px] text-slate-800 outline-none transition focus:border-[#9DB0E4]';

const BusinessAssessment: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();

  /** `s` و `i` نسخهٔ کوتاه `slug` و `invite` هستند تا لینک پیامک کوتاه‌تر شود */
  const slugParam = searchParams.get('slug') || searchParams.get('s') || undefined;
  const inviteParam = searchParams.get('invite') || searchParams.get('i') || undefined;

  const [mode, setMode] = useState<Mode>('loading');
  const [error, setError] = useState('');
  const [landing, setLanding] = useState<BusinessLanding | null>(null);
  const [data, setData] = useState<BusinessFormPayload | null>(null);
  const [result, setResult] = useState<BusinessCaseResult | null>(null);

  const [caseId, setCaseId] = useState('');
  const [profile, setProfile] = useState<BusinessProfile>({});
  const [answers, setAnswers] = useState<Record<string, { score: number; evidence?: string | null }>>({});
  const [metrics, setMetrics] = useState<
    Record<string, { current?: string | null; target?: string | null; source?: string | null; notMeasured: boolean }>
  >({});
  const [flags, setFlags] = useState<Record<string, { value: boolean; note?: string | null }>>({});

  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const axes = useMemo(() => data?.axes ?? [], [data]);
  const evidenceFrom = data?.evidencePromptFrom ?? 4;

  /** A → B → شش محور C → E */
  const steps = useMemo(
    () => [
      { key: 'profile', title: 'اطلاعات پایه' },
      { key: 'metrics', title: 'تصویر عددی' },
      ...axes.map((a) => ({ key: `axis:${a.id}`, title: a.title })),
      { key: 'flags', title: 'پرچم‌های قرمز' },
    ],
    [axes],
  );
  const currentStep = steps[stepIndex];

  const totalStatements = useMemo(
    () => axes.reduce((sum, a) => sum + a.statements.length, 0),
    [axes],
  );
  const answeredCount = useMemo(
    () => axes.flatMap((a) => a.statements).filter((s) => answers[s.id]?.score).length,
    [axes, answers],
  );
  const progress = steps.length
    ? Math.round(((stepIndex + (currentStep?.key.startsWith('axis') ? 0 : 1)) / steps.length) * 100)
    : 0;

  // ── بارگذاری ───────────────────────────────────────────────────────────────

  const loadGuest = useCallback(async () => {
    try {
      setLanding(await businessService.getLanding(slugParam));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'فرم فعالی برای نمایش وجود ندارد.');
    }
    setMode('guest');
  }, [slugParam]);

  const hydrate = useCallback((payload: BusinessFormPayload) => {
    setData(payload);
    if (payload.draft) {
      setCaseId(payload.draft.id);
      setProfile(payload.draft.profile || {});
      setAnswers(payload.draft.answers || {});
      setMetrics(payload.draft.metrics || {});
      setFlags(payload.draft.flags || {});
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;

    (async () => {
      if (!user) {
        await loadGuest();
        return;
      }
      try {
        const payload = await businessService.getForm(slugParam, inviteParam);
        if (cancelled) return;
        hydrate(payload);

        if (payload.draft) {
          setMode('wizard');
        } else if (payload.submittedCase) {
          setResult(await businessService.getMyCase());
          if (!cancelled) setMode('result');
        } else {
          setMode('intro');
        }
      } catch (err: any) {
        if (cancelled) return;
        if (err?.response?.status === 403) {
          setError(err?.response?.data?.message || 'دسترسی به این فرم ندارید.');
          setMode('locked');
          return;
        }
        setError(err?.response?.data?.message || 'دریافت فرم انجام نشد.');
        setMode('intro');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, user, slugParam, inviteParam, loadGuest, hydrate]);

  // ── ذخیرهٔ خودکار ──────────────────────────────────────────────────────────

  /**
   * هر مرحله فقط دادهٔ خودش را می‌فرستد تا درخواست‌ها سبک بمانند.
   * خروجی می‌گوید ذخیره موفق بود یا نه؛ شکست نباید بی‌صدا بماند چون
   * دادهٔ همان مرحله روی سرور نمی‌نشیند و دیگر هم دوباره فرستاده نمی‌شود.
   */
  const flush = useCallback(async (): Promise<boolean> => {
    if (!caseId || !currentStep) return true;
    setSaveState('saving');
    try {
      if (currentStep.key === 'profile') {
        await businessService.saveProfile(caseId, profile);
      } else if (currentStep.key === 'metrics') {
        await businessService.saveProfile(caseId, {
          positiveTrend: profile.positiveTrend,
          worryingTrend: profile.worryingTrend,
          unmeasuredNumber: profile.unmeasuredNumber,
        });
        const rows = Object.entries(metrics).map(([metricId, v]) => ({
          metricId,
          current: v.current ?? undefined,
          target: v.target ?? undefined,
          source: v.source ?? undefined,
          notMeasured: v.notMeasured,
        }));
        if (rows.length) await businessService.saveMetrics(caseId, rows);
      } else if (currentStep.key === 'flags') {
        const rows = Object.entries(flags).map(([flagId, v]) => ({
          flagId,
          value: v.value,
          note: v.note ?? undefined,
        }));
        if (rows.length) await businessService.saveFlags(caseId, rows);
      } else {
        const axisId = currentStep.key.slice('axis:'.length);
        const axis = axes.find((a) => a.id === axisId);
        const rows = (axis?.statements ?? [])
          .filter((s) => answers[s.id]?.score)
          .map((s) => ({
            statementId: s.id,
            score: answers[s.id].score,
            evidence: answers[s.id].evidence ?? undefined,
          }));
        if (rows.length) await businessService.saveAnswers(caseId, rows);
      }
      setSaveState('saved');
      setError((prev) => (prev === SAVE_FAILED ? '' : prev));
      return true;
    } catch {
      setSaveState('error');
      setError(SAVE_FAILED);
      return false;
    }
  }, [caseId, currentStep, profile, metrics, flags, answers, axes]);

  const queueSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, 900);
  }, [flush]);

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    [],
  );

  // ── اکشن‌ها ────────────────────────────────────────────────────────────────

  const handleStart = async () => {
    if (!data) return;
    setError('');
    try {
      const created = await businessService.startCase(data.form.id, inviteParam);
      setCaseId(created.id);
      setStepIndex(0);
      setMode('wizard');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'شروع فرم انجام نشد.');
      if (err?.response?.status === 403) setMode('locked');
    }
  };

  const stepComplete = useMemo(() => {
    if (!currentStep) return false;
    if (currentStep.key === 'profile') return !!profile.businessName?.trim();
    if (currentStep.key === 'metrics') return true; // بخش B عمداً اختیاری است
    if (currentStep.key === 'flags') {
      return (data?.redFlags ?? []).every((f) => flags[f.id] !== undefined);
    }
    /** فقط امتیازدادن به همهٔ گزاره‌ها لازم است؛ شاهد اختیاری است */
    const axisId = currentStep.key.slice('axis:'.length);
    const axis = axes.find((a) => a.id === axisId);
    return (axis?.statements ?? []).every((s) => !!answers[s.id]?.score);
  }, [currentStep, profile, flags, data, axes, answers]);

  const handleNext = async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    /** تا وقتی دادهٔ این مرحله روی سرور ننشسته، جلو نمی‌رویم */
    if (!(await flush())) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (stepIndex < steps.length - 1) {
      setStepIndex((i) => i + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      setResult(await businessService.submit(caseId));
      setMode('result');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'ثبت نهایی انجام نشد.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── نماها ──────────────────────────────────────────────────────────────────

  if (authLoading || mode === 'loading') {
    return (
      <div className="min-h-screen bg-[#FAFBFE]">
        <SiteBrandedLoader />
      </div>
    );
  }

  const ErrorBox = error ? (
    <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm font-bold text-red-700">
      {error}
    </div>
  ) : null;

  if (mode === 'guest') {
    const loginUrl = buildLoginUrl(currentPathForNext(location));
    return (
      <Shell>
        {ErrorBox}
        {landing ? (
          <Panel className="overflow-hidden">
            <div
              className="px-5 py-9 text-center text-white sm:px-10"
              style={{ background: 'linear-gradient(140deg,#16307F 0%,#1C3B9B 50%,#4B2FA8 100%)' }}
            >
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/12 px-3 py-1 text-[11px] font-black">
                <Building2 className="h-3.5 w-3.5" />
                ابزار پیش‌تشخیص
              </span>
              <h1 className="mt-4 text-2xl font-black sm:text-[30px]">{landing.title}</h1>
              {landing.description ? (
                <p className="mx-auto mt-3 max-w-xl text-[13.5px] leading-8 text-white/85">
                  {landing.description}
                </p>
              ) : null}
            </div>

            <div className="grid grid-cols-3 border-t" style={{ borderColor: HAIRLINE }}>
              {[
                { icon: <Gauge className="h-4 w-4" />, label: 'محور', value: fa(landing.axisCount) },
                { icon: <ClipboardList className="h-4 w-4" />, label: 'گزاره', value: fa(landing.statementCount) },
                { icon: <Clock3 className="h-4 w-4" />, label: 'دقیقه', value: fa(landing.estimatedMinutes) },
              ].map((tile) => (
                <div key={tile.label} className="px-3 py-5 text-center">
                  <span className="mx-auto flex h-9 w-9 items-center justify-center text-[#1C3B9B]">
                    {tile.icon}
                  </span>
                  <p className="mt-1.5 text-2xl font-black text-slate-900">{tile.value}</p>
                  <p className="mt-0.5 text-[11px] font-bold text-slate-400">{tile.label}</p>
                </div>
              ))}
            </div>

            <PanelDivider />
            <div className="p-5 sm:p-7">
              {landing.privacyNote ? (
                <p
                  className="flex items-start gap-2 rounded-2xl border px-4 py-3 text-[12px] leading-7 text-slate-600"
                  style={{ borderColor: HAIRLINE }}
                >
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#1C3B9B]" />
                  {landing.privacyNote}
                </p>
              ) : null}

              <div className="mt-5 text-center">
                <span
                  className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border text-[#1C3B9B]"
                  style={{ borderColor: HAIRLINE }}
                >
                  <Lock className="h-5 w-5" />
                </span>
                <p className="mt-3 text-[15px] font-black text-slate-900">
                  برای تکمیل فرم ابتدا وارد شوید
                </p>
                <p className="mx-auto mt-1.5 max-w-md text-[12px] font-bold leading-7 text-slate-500">
                  بلافاصله بعد از ورود به همین صفحه برمی‌گردید و پاسخ‌ها ذخیره می‌شوند.
                </p>
                {landing.inviteOnly && !inviteParam ? (
                  <p className="mx-auto mt-2 max-w-md rounded-xl border border-amber-200 px-3 py-2 text-[11.5px] font-black leading-6 text-amber-700">
                    این فرم فقط با لینک دعوت باز می‌شود. حتماً روی لینک کامل کلیک کنید.
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={() => navigate(loginUrl)}
                  className="mt-4 inline-flex items-center gap-2 rounded-2xl px-7 py-3 text-sm font-black text-white shadow-[0_18px_34px_-18px_rgba(28,59,155,0.9)] transition hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg,#1C3B9B,#3A2FA0)' }}
                >
                  <LogIn className="h-4 w-4" />
                  ورود یا ثبت‌نام
                </button>
              </div>
            </div>
          </Panel>
        ) : null}
      </Shell>
    );
  }

  if (mode === 'locked') {
    return (
      <Shell>
        <Panel className="overflow-hidden">
          <div
            className="px-5 py-8 text-center text-white sm:px-8"
            style={{ background: 'linear-gradient(140deg,#16307F 0%,#1C3B9B 50%,#4B2FA8 100%)' }}
          >
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-white/25 bg-white/12">
              <Lock className="h-6 w-6" />
            </span>
            <h1 className="mt-3 text-xl font-black sm:text-2xl">این فرم دعوت‌نامه‌ای است</h1>
            <p className="mx-auto mt-2 max-w-md text-[13px] leading-8 text-white/85">
              {error || 'برای تکمیل این فرم باید لینک اختصاصی آن را دریافت کرده باشید.'}
            </p>
          </div>
          <div className="p-6 text-center">
            <Link
              to="/dashboard"
              className="inline-block rounded-2xl px-5 py-3 text-sm font-black text-white"
              style={{ background: 'linear-gradient(135deg,#1C3B9B,#3A2FA0)' }}
            >
              بازگشت به داشبورد
            </Link>
          </div>
        </Panel>
      </Shell>
    );
  }

  if (mode === 'intro' && data) {
    return (
      <Shell>
        {ErrorBox}
        <Panel className="overflow-hidden">
          <div
            className="px-5 py-7 text-white sm:px-8"
            style={{ background: 'linear-gradient(140deg,#16307F 0%,#1C3B9B 50%,#4B2FA8 100%)' }}
          >
            <h1 className="text-2xl font-black sm:text-[28px]">{data.form.title}</h1>
            {data.form.introText ? (
              <p className="mt-2.5 max-w-2xl text-[13px] leading-8 text-white/85">
                {data.form.introText}
              </p>
            ) : null}
          </div>
          <div className="p-5 sm:p-7">
            {data.form.privacyNote ? (
              <p
                className="flex items-start gap-2 rounded-2xl border px-4 py-3 text-[12px] leading-7 text-slate-600"
                style={{ borderColor: HAIRLINE }}
              >
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#1C3B9B]" />
                {data.form.privacyNote}
              </p>
            ) : null}
            <div className="mt-4 space-y-2">
              {steps.map((step, index) => (
                <div
                  key={step.key}
                  className="flex items-center gap-3 rounded-2xl border px-4 py-2.5"
                  style={{ borderColor: HAIRLINE }}
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[11px] font-black text-white" style={{ backgroundColor: BRAND }}>
                    {fa(index + 1)}
                  </span>
                  <span className="text-[13px] font-black text-slate-800">{step.title}</span>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={handleStart}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-sm font-black text-white shadow-[0_18px_34px_-18px_rgba(28,59,155,0.9)] transition hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg,#1C3B9B,#3A2FA0)' }}
            >
              شروع فرم
              <ArrowLeft className="h-4 w-4" />
            </button>
          </div>
        </Panel>
      </Shell>
    );
  }

  if (mode === 'wizard' && data && currentStep) {
    const isLast = stepIndex === steps.length - 1;
    const axisId = currentStep.key.startsWith('axis:') ? currentStep.key.slice(5) : null;
    const axis = axisId ? axes.find((a) => a.id === axisId) : null;

    return (
      <Shell>
        <div
          className="sticky top-0 z-20 -mx-4 mb-4 border-b bg-[#FAFBFE]/88 px-4 py-3 backdrop-blur-md sm:-mx-6 sm:px-6"
          style={{ borderColor: HAIRLINE }}
        >
          <div className="flex items-center gap-3">
            <ProgressRing value={progress} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13.5px] font-black text-slate-900">{currentStep.title}</p>
              <p className="mt-0.5 text-[11px] font-bold text-slate-400">
                مرحله {fa(stepIndex + 1)} از {fa(steps.length)}
                {totalStatements ? ` · ${fa(answeredCount)} از ${fa(totalStatements)} گزاره` : ''}
              </p>
            </div>
            {saveState === 'saving' ? (
              <span className="text-[10.5px] font-black text-slate-400">در حال ذخیره…</span>
            ) : saveState === 'saved' ? (
              <span className="inline-flex items-center gap-1 text-[10.5px] font-black text-emerald-600">
                <Check className="h-3.5 w-3.5" />
                ذخیره شد
              </span>
            ) : saveState === 'error' ? (
              <span className="inline-flex items-center gap-1 text-[10.5px] font-black text-red-600">
                <AlertTriangle className="h-3.5 w-3.5" />
                ذخیره نشد
              </span>
            ) : null}
          </div>
        </div>

        {ErrorBox}

        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep.key}
            initial={{ opacity: 0, x: -18 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 18 }}
            transition={{ duration: 0.22 }}
            className="space-y-3"
          >
            {/* ── بخش A ── */}
            {currentStep.key === 'profile' ? (
              <Panel className="p-5">
                <h2 className="text-lg font-black text-slate-900">اطلاعات پایه کسب‌وکار</h2>
                <p className="mt-1 text-[11.5px] font-bold text-slate-400">
                  فقط نام کسب‌وکار الزامی است؛ بقیه هرچه دقیق‌تر، تحلیل بهتر.
                </p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {PROFILE_FIELDS.map((field) => (
                    <label key={field.key} className={field.long ? 'sm:col-span-2' : ''}>
                      <span className="mb-1 block text-[11.5px] font-black text-slate-600">
                        {field.label}
                        {field.key === 'businessName' ? (
                          <span className="text-red-500"> *</span>
                        ) : null}
                      </span>
                      {field.long ? (
                        <textarea
                          rows={2}
                          value={(profile[field.key] as string) || ''}
                          onChange={(e) => {
                            setProfile((p) => ({ ...p, [field.key]: e.target.value }));
                            queueSave();
                          }}
                          className={inputClass}
                          style={{ borderColor: HAIRLINE }}
                        />
                      ) : (
                        <input
                          value={(profile[field.key] as string) || ''}
                          placeholder={field.placeholder}
                          maxLength={field.max}
                          onChange={(e) => {
                            setProfile((p) => ({ ...p, [field.key]: e.target.value }));
                            queueSave();
                          }}
                          className={inputClass}
                          style={{ borderColor: HAIRLINE }}
                        />
                      )}
                    </label>
                  ))}
                </div>
              </Panel>
            ) : null}

            {/* ── بخش B ── */}
            {currentStep.key === 'metrics' ? (
              <>
                <Panel className="p-5">
                  <h2 className="text-lg font-black text-slate-900">تصویر عددی وضعیت فعلی</h2>
                  <p className="mt-1 text-[11.5px] font-bold leading-6 text-slate-500">
                    عدد دقیق ندارید؟ بازهٔ تقریبی بنویسید و منبعش را مشخص کنید. اگر اصلاً
                    اندازه‌گیری نمی‌شود، تیک «اندازه نمی‌گیریم» را بزنید — حدس نزنید.
                  </p>

                  <div className="mt-4 space-y-2.5">
                    {data.metrics.map((metric) => {
                      const value = metrics[metric.id] || { notMeasured: false };
                      const off = value.notMeasured;
                      return (
                        <div
                          key={metric.id}
                          className="rounded-2xl border p-3"
                          style={{ borderColor: HAIRLINE }}
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-[12.5px] font-black text-slate-800">
                              {metric.title}
                            </p>
                            <label className="flex cursor-pointer items-center gap-1.5 text-[11px] font-bold text-slate-500">
                              <input
                                type="checkbox"
                                checked={off}
                                onChange={(e) => {
                                  /* با زدن تیک، عددهای قبلی هم پاک می‌شوند تا
                                     پروندهٔ منتور «اندازه نمی‌گیریم» و یک عدد
                                     قدیمی را هم‌زمان نشان ندهد */
                                  setMetrics((m) => ({
                                    ...m,
                                    [metric.id]: e.target.checked
                                      ? { current: null, target: null, source: null, notMeasured: true }
                                      : { ...value, notMeasured: false },
                                  }));
                                  queueSave();
                                }}
                              />
                              اندازه نمی‌گیریم
                            </label>
                          </div>
                          {metric.helpText ? (
                            <p className="mt-0.5 text-[10.5px] font-bold text-slate-400">
                              {metric.helpText}
                            </p>
                          ) : null}
                          {!off ? (
                            <div className="mt-2.5 grid gap-2 sm:grid-cols-3">
                              {(
                                [
                                  ['current', 'وضعیت فعلی'],
                                  ['target', 'هدف ۱۲ ماهه'],
                                  ['source', 'منبع داده / توضیح'],
                                ] as const
                              ).map(([key, label]) => (
                                <label key={key}>
                                  <span className="mb-1 block text-[10.5px] font-black text-slate-500">
                                    {label}
                                  </span>
                                  <input
                                    value={(value as any)[key] || ''}
                                    maxLength={METRIC_MAX[key]}
                                    onChange={(e) => {
                                      setMetrics((m) => ({
                                        ...m,
                                        [metric.id]: { ...value, [key]: e.target.value },
                                      }));
                                      queueSave();
                                    }}
                                    className={`${inputClass} py-2 text-[12px]`}
                                    style={{ borderColor: HAIRLINE }}
                                  />
                                </label>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </Panel>

                <Panel className="p-5">
                  <h3 className="text-[15px] font-black text-slate-900">سه واقعیت مهم پشت اعداد</h3>
                  <div className="mt-3 space-y-3">
                    {FACT_FIELDS.map((field) => (
                      <label key={field.key} className="block">
                        <span className="mb-1 block text-[11.5px] font-black text-slate-600">
                          {field.label}
                        </span>
                        <textarea
                          rows={2}
                          value={(profile[field.key] as string) || ''}
                          onChange={(e) => {
                            setProfile((p) => ({ ...p, [field.key]: e.target.value }));
                            queueSave();
                          }}
                          className={inputClass}
                          style={{ borderColor: HAIRLINE }}
                        />
                      </label>
                    ))}
                  </div>
                </Panel>
              </>
            ) : null}

            {/* ── بخش C: یک محور ── */}
            {axis ? (
              <>
                <Panel className="overflow-hidden">
                  <div className="flex items-center gap-4 px-5 py-4">
                    <span
                      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-[15px] font-black text-white"
                      style={{ background: 'linear-gradient(135deg,#1C3B9B,#3A2FA0)' }}
                    >
                      {axis.title.slice(0, 2)}
                    </span>
                    <div className="min-w-0">
                      <h2 className="text-lg font-black text-slate-900">{axis.title}</h2>
                      <p className="mt-0.5 text-[11.5px] font-bold text-slate-400">
                        {axis.subtitle} · {fa(axis.statements.length)} گزاره
                      </p>
                    </div>
                  </div>
                  <PanelDivider />
                  <div className="px-5 py-3.5">
                    <ScaleLegend labels={[SCALE_LABELS[0], SCALE_LABELS[4]]} />
                  </div>
                </Panel>

                {axis.statements.map((statement, index) => {
                  const answer = answers[statement.id];
                  /** برای امتیازهای بالا خانهٔ شاهد پیشنهاد می‌شود، ولی اجباری نیست */
                  const showEvidence = (answer?.score ?? 0) >= evidenceFrom;

                  return (
                    <div
                      key={statement.id}
                      className="rounded-[26px] border bg-white px-4 py-4 sm:px-5"
                      style={{ borderColor: HAIRLINE }}
                    >
                      <div className="mb-4 flex items-start gap-2.5">
                        <span
                          className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border text-[11px] font-black"
                          style={
                            answer?.score
                              ? { backgroundColor: BRAND, borderColor: BRAND, color: '#fff' }
                              : { borderColor: HAIRLINE, color: '#94A3B8' }
                          }
                        >
                          {answer?.score ? <Check className="h-3.5 w-3.5" /> : fa(index + 1)}
                        </span>
                        <p className="text-[14.5px] font-bold leading-8 text-slate-800">
                          {statement.text}
                        </p>
                      </div>

                      <LikertScale
                        name={statement.text}
                        labels={SCALE_LABELS}
                        value={answer?.score}
                        onChange={(score) => {
                          setAnswers((a) => ({
                            ...a,
                            [statement.id]: { ...(a[statement.id] || {}), score },
                          }));
                          queueSave();
                        }}
                      />

                      {showEvidence ? (
                        <div className="mt-3.5">
                          <label className="block">
                            <span className="mb-1 block text-[11px] font-black text-slate-500">
                              شاهد این امتیاز — اختیاری، ولی به دقت تحلیل کمک می‌کند
                            </span>
                            <input
                              value={answer?.evidence || ''}
                              placeholder="نام گزارش، عدد، یا یک توضیح کوتاه"
                              onChange={(e) => {
                                setAnswers((a) => ({
                                  ...a,
                                  [statement.id]: { ...a[statement.id], evidence: e.target.value },
                                }));
                                queueSave();
                              }}
                              className={inputClass}
                              style={{ borderColor: HAIRLINE }}
                            />
                          </label>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </>
            ) : null}

            {/* ── بخش E ── */}
            {currentStep.key === 'flags' ? (
              <Panel className="p-5">
                <h2 className="text-lg font-black text-slate-900">پرچم‌های قرمز فوری</h2>
                <p className="mt-1 text-[11.5px] font-bold leading-6 text-slate-500">
                  هر «بله» اولویت را از رشد به تثبیت و کاهش ریسک تغییر می‌دهد. صادقانه پاسخ
                  دهید — این بخش تعیین می‌کند جلسهٔ شما از کجا شروع شود.
                </p>
                <div className="mt-4 space-y-2.5">
                  {data.redFlags.map((flag) => {
                    const answer = flags[flag.id];
                    return (
                      <div
                        key={flag.id}
                        className="rounded-2xl border p-3.5"
                        style={{ borderColor: answer?.value ? '#F3C9C9' : HAIRLINE }}
                      >
                        <p className="text-[12.5px] font-bold leading-7 text-slate-800">
                          {flag.text}
                        </p>
                        <div className="mt-2.5 flex gap-2">
                          {[
                            { value: true, label: 'بله', color: '#d03b3b' },
                            { value: false, label: 'خیر', color: '#0ca30c' },
                          ].map((option) => {
                            const active = answer?.value === option.value;
                            return (
                              <button
                                key={option.label}
                                type="button"
                                onClick={() => {
                                  setFlags((f) => ({
                                    ...f,
                                    [flag.id]: { ...(f[flag.id] || {}), value: option.value },
                                  }));
                                  queueSave();
                                }}
                                className="rounded-xl border px-5 py-1.5 text-[12px] font-black transition"
                                style={
                                  active
                                    ? {
                                        borderColor: option.color,
                                        backgroundColor: `${option.color}12`,
                                        color: option.color,
                                      }
                                    : { borderColor: HAIRLINE, color: '#94A3B8' }
                                }
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                        {answer?.value ? (
                          <input
                            value={answer.note || ''}
                            placeholder="توضیح کوتاه (اختیاری ولی خیلی کمک می‌کند)"
                            onChange={(e) => {
                              setFlags((f) => ({
                                ...f,
                                [flag.id]: { ...f[flag.id], note: e.target.value },
                              }));
                              queueSave();
                            }}
                            className={`${inputClass} mt-2.5`}
                            style={{ borderColor: HAIRLINE }}
                          />
                        ) : null}
                      </div>
                    );
                  })}
                </div>

              </Panel>
            ) : null}
          </motion.div>
        </AnimatePresence>

        <div
          className="sticky bottom-0 z-20 -mx-4 mt-5 border-t bg-[#FAFBFE]/90 px-4 py-3 backdrop-blur-md sm:-mx-6 sm:px-6"
          style={{ borderColor: HAIRLINE }}
        >
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              disabled={stepIndex === 0}
              onClick={() => {
                setStepIndex((i) => Math.max(0, i - 1));
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="flex shrink-0 items-center gap-1.5 rounded-2xl border border-[#C8D6FF] bg-white px-4 py-3 text-sm font-black text-[#1C3B9B] disabled:opacity-40"
            >
              <ArrowRight className="h-4 w-4" />
              <span className="hidden sm:inline">قبلی</span>
            </button>
            <button
              type="button"
              disabled={!stepComplete || submitting}
              onClick={handleNext}
              className="flex flex-1 items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-black text-white transition disabled:cursor-not-allowed disabled:opacity-45"
              style={{
                background: stepComplete ? 'linear-gradient(135deg,#1C3B9B,#3A2FA0)' : '#94A3B8',
              }}
            >
              {submitting
                ? 'در حال ثبت…'
                : !stepComplete
                  ? currentStep.key === 'profile'
                    ? 'نام کسب‌وکار را وارد کنید'
                    : 'همهٔ موارد این مرحله را کامل کنید'
                  : isLast
                    ? 'ثبت نهایی و مشاهدهٔ نتیجه'
                    : 'مرحلهٔ بعد'}
              {!submitting && stepComplete && !isLast ? <ArrowLeft className="h-4 w-4" /> : null}
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  if (mode === 'result' && result) {
    return (
      <Shell wide>
        {ErrorBox}
        <div className="space-y-4">
          <Panel className="overflow-hidden">
            <div
              className="px-5 py-8 text-center text-white sm:px-8"
              style={{ background: 'linear-gradient(140deg,#16307F 0%,#1C3B9B 50%,#4B2FA8 100%)' }}
            >
              <CheckCircle2 className="mx-auto h-9 w-9" />
              <h1 className="mt-2.5 text-2xl font-black">ارزیابی شما ثبت شد</h1>
              <p className="mx-auto mt-2 max-w-lg text-[13px] leading-8 text-white/85">
                نتیجه برای منتور مجموعه ارسال شد. در جلسهٔ تشخیص، سه اولویت ۹۰ روزهٔ شما
                مشخص می‌شود.
              </p>
            </div>
            {result.overallScore !== null && result.band ? (
              <div className="p-5 sm:p-7">
                <BusinessOverallGauge
                  score={result.overallScore}
                  band={result.band}
                  bandLabel={result.bandLabel || ''}
                  direction={result.direction}
                />
              </div>
            ) : null}
          </Panel>

          {result.stabilizeFirst ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3.5">
              <p className="flex items-center gap-2 text-[13px] font-black text-red-800">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {fa(result.redFlagCount)} پرچم قرمز ثبت شده است
              </p>
              <p className="mt-1 text-[11.5px] font-bold leading-7 text-red-700">
                طبق قاعدهٔ این ارزیابی، تا وقتی این موارد رسیدگی نشوند اولویت شما «تثبیت و
                کاهش ریسک» است، نه رشد.
              </p>
            </div>
          ) : null}

          <Panel className="p-4 sm:p-5">
            <p className="mb-3 text-[14px] font-black text-slate-900">وضعیت شش محور</p>
            <BusinessAxisChart scores={result.axisScores} />
          </Panel>

          <div className="grid gap-3 sm:grid-cols-2">
            {[
              { label: 'قوی‌ترین محور', axis: result.strongest },
              { label: 'ضعیف‌ترین محور', axis: result.weakest },
            ].map((item) =>
              item.axis ? (
                <Panel key={item.label} className="p-4">
                  <p className="text-[11px] font-black text-slate-400">{item.label}</p>
                  <p className="mt-1 text-[15px] font-black text-slate-900">{item.axis.title}</p>
                  <p className="text-[11.5px] font-bold text-slate-500">{item.axis.subtitle}</p>
                  <div className="mt-2">
                    <BandChip band={item.axis.band} />
                  </div>
                  <p className="mt-2 text-[11.5px] font-bold leading-7 text-slate-500">
                    {item.axis.direction}
                  </p>
                </Panel>
              ) : null,
            )}
          </div>

          <div className="flex flex-col gap-2.5 sm:flex-row">
            <Link
              to="/dashboard"
              className="flex-1 rounded-2xl px-4 py-3.5 text-center text-sm font-black text-white"
              style={{ background: 'linear-gradient(135deg,#1C3B9B,#3A2FA0)' }}
            >
              بازگشت به داشبورد
            </Link>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {ErrorBox}
      <Panel className="p-10 text-center">
        <p className="text-sm font-bold text-slate-500">
          در حال حاضر فرم فعالی برای نمایش وجود ندارد.
        </p>
      </Panel>
    </Shell>
  );
};

export default BusinessAssessment;
